import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords, update } from '../services/MessagesService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessageStatus, Message } from '../types';
import { useMessagesSelection } from './useMessagesSelection';

export const useMessages = () => {
    const queryClient = useQueryClient();
    const { status, id: aliasId, tag: tagId } = useParams();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const { selection } = useMessagesSelection();

    // Build filter based on URL params
    const filter: { status?: string; aliasId?: string; tagId?: string } = {};
    if (status) {
        if (status === 'inbox') {
            filter.status = MessageStatus.UNREAD;
        } else if (status === 'read') {
            filter.status = MessageStatus.READ;
        } else if (status === 'sent') {
            filter.status = MessageStatus.SENT;
        } else if (status === 'deleted') {
            filter.status = MessageStatus.DELETED;
        } else if (status === 'archived') {
            filter.status = MessageStatus.ARCHIVED;
        }
    }
    if (aliasId) {
        filter.aliasId = aliasId;
    }
    if (tagId) {
        filter.tagId = tagId;
    }

    // INFINITE QUERY
    const queryKey = ['messages', filter.status || null, filter.aliasId || null, filter.tagId || null, keyword, limit];
    
    const messagesQuery = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ 
            ...filter, 
            limit,
            nextToken: pageParam 
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 0
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: async ({ id, record }: { id: string; record: Message }) => {
            return update(id, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
    });

    const fetchRecordsAction = () => {
        if (messagesQuery.hasNextPage) {
            messagesQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const updateRecord = (id: string, record: Message) => {
        updateMutation.mutate({ id, record });
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = messagesQuery.data?.pages?.flatMap(page => 
        (page && 'items' in page) ? page.items : []
    ) || [];

    const refetchRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['messages'] });
    };

    return {
        items: allItems,
        visibleItems: allItems,
        loading: messagesQuery.isLoading,
        isFetchingNextPage: messagesQuery.isFetchingNextPage,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: messagesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        refetchRecords,
        deleteRecords,
        updateRecord,
        handleFilterByKeyword,
        deleteMutation,
        updateMutation
    };
};
